import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useStateMachine } from 'little-state-machine';
import isEmpty from 'lodash.isempty';

import updateFormAction from 'utils/updateFormAction';

import { PlanType, getPlans, mergeSortPlans, savePlanDetails, trackEvent } from 'v6/api/signup';

import { CURRENCIES, PERIODS, PERIOD_MONTH, getContentHeightCss } from 'constants/common';

import { FormDataProps } from 'utils/types';

import { CheckIcon, LinkExternalIcon } from 'assets/icons';

import {
  PlanComponent,
  RootWrap,
  Wrap,
  PlanWrap,
  Plan,
  ButtonWrapStyled,
  ButtonStyled,
  Subtitle,
  OptionsWrap,
  CurrencyOptions,
  OptionItem,
  BillingCycleOptions,
  CurrencyOptionsWrap,
  BillingCycleOptionsWrap,
  LinkSection,
  FullPricingLink,
  PlanSelectedIndicator,
  PlanName,
  PlanPrice,
  PlanPriceDetail,
  BilledPerYear,
  FeaturesWrap,
  Features,
  FeaturesTitle,
  SpinnerWrap,
  Strikethrough,
  PreviousNavWrap,
} from 'v6/Pages/ChoosePlan/ChooseTrial/styled';

import { Title, TitleMobile } from 'v6/Pages/Homepage/styled';
import { ContactSection } from 'v6/Pages/Homepage/Actions';
import { Spinner } from 'v6/Components/Spinner';
import { PreviousNav } from 'v6/Components/PreviousNav';
import { CURRENCIES_PREFIX } from 'v6/Pages/PaymentDetails/OrderSummary';
import { Alert } from '../../../Components/Alert';
import styled from 'styled-components';
import { bugsnagNotify } from 'utils/error';
import { Container } from 'v6/styled';

const ChooseTrialFromSessionStorageKey = 'ChooseTrialFrom';

export const DEFAULT_PERIOD = PERIOD_MONTH;

export type PlanNameTypes = 'fundamentals' | 'essentials' | 'professional' | 'enterprise';

export interface LocalFormDataProps {
  currency: string;
  period: 'month' | 'year';
}

const ChooseTrial: React.FC = () => {
  const { actions, state } = useStateMachine({ updateFormAction });
  const defaultValuesFromStorage = state as FormDataProps;
  const { plans, user, numberAccounts, country } = defaultValuesFromStorage;
  const selectedCurrency: string = defaultValuesFromStorage.currency;

  const history = useHistory();
  const [isFormValid, setIsFormValid] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [serverError, setServerError] = useState<string>('');
  const [isSubmittingForm, setIsSubmittingForm] = useState<boolean>(false);
  const [isGettingPlans, setIsGettingPlans] = useState(false);

  const [formData, setformData] = useState<LocalFormDataProps>({
    currency: selectedCurrency || 'usd',
    period: DEFAULT_PERIOD,
  });

  const [currentPlans, setCurrentPlans] = useState<PlanType[]>(
    mergeSortPlans(plans, numberAccounts)['month'],
  );
  const [selectedPlan, setSelectedPlan] = useState<PlanType | undefined>();

  const handleChange = (key: string, value: string) => {
    const newFrom = {
      ...formData,
      [key]: value,
    };
    sessionStorage.setItem(ChooseTrialFromSessionStorageKey, JSON.stringify(newFrom));
    setformData(newFrom);
    if (key === 'currency') {
      actions.updateFormAction({ currency: value });
    }
  };

  const hidePlanBasedonNumberAccounts = (plans: Array<any>) => {
    if (Number(numberAccounts) === 1) {
      return plans.filter(plan => !plan.id.includes('sked-enterprise'));
    } else {
      return plans.filter(plan => !plan.id.includes('sked-fundamentals'));
    }
  };

  useEffect(() => {
    const init = async () => {
      try {
        setIsGettingPlans(true);
        const plans = await getPlans({
          id: user._id,
          currency: formData.currency,
          numberAccounts,
          country: country,
        });

        setIsGettingPlans(false);
        if (formData.period === 'month') {
          setCurrentPlans(hidePlanBasedonNumberAccounts(plans.month));
        } else {
          setCurrentPlans(hidePlanBasedonNumberAccounts(plans.year));
        }
      } catch (error: any) {
        bugsnagNotify(error, 'Error getting plans');
        setServerError(error.message);
        console.error(error);
      } finally {
        setIsGettingPlans(false);
      }
    };

    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.period, formData.currency, numberAccounts, user._id]);

  useEffect(() => {
    if (selectedPlan) {
      const planPrefix = selectedPlan.id.replace('-annual', '');
      const nextPlan = currentPlans.find(p => p.id.includes(planPrefix));
      setSelectedPlan(nextPlan);
      actions.updateFormAction({ selectedPlanId: nextPlan?.id });
    } else {
      const planId = Number(numberAccounts) > 1 ? 'sked-professional' : 'sked-essentials';
      const defaultPlan = currentPlans.find(plan => plan.id === planId);
      setSelectedPlan(defaultPlan || currentPlans[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPlans, numberAccounts]);

  useEffect(() => {
    const { currency, period } = formData;
    const isformValid = !!currency && !!period && !!selectedPlan;
    setIsFormValid(isformValid);
  }, [formData, selectedPlan]);

  useEffect(() => {
    const valueString = sessionStorage.getItem(ChooseTrialFromSessionStorageKey);
    if (valueString) {
      const form = JSON.parse(valueString);
      setformData(form);
    }
  }, []);

  const { state: prevPageFormData } = useStateMachine({ updateFormAction });

  //bring user back to home page if user didn't fill out form at home page
  useEffect(() => {
    if (isEmpty(prevPageFormData)) {
      history.replace('/');
    }
  }, [prevPageFormData, history]);

  useEffect(() => {
    trackEvent({
      event: 'sign_up-choose_trial-view-page',
    });

    trackEvent({
      event: 'sign-up-24-step-2',
    });
  }, []);

  const onSubmit = async () => {
    if (!selectedPlan) {
      return;
    }

    setIsSubmitting(true);
    actions.updateFormAction({ selectedPlanId: selectedPlan.id });
    actions.updateFormAction({ currency: formData.currency });
    actions.updateFormAction({ billingPeriod: formData.period });

    try {
      // // according to "class Chargebee" initialization, it's work on DOMContentLoaded and initiated only
      // // when the full page was loaded or reloaded. It's not work when we use react routing as expected.
      savePlanDetails(selectedPlan.id, formData.currency);
      history.push({ pathname: '/payment' });
    } catch (error: any) {
      setServerError(error.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSwitchPricingType = () => {
    const type = formData.period === 'month' ? 'monthly' : 'annual';
    trackEvent({
      event: `sign_up-choose_trial-${type}_billing`,
    });
  };

  const handlePlanClick = (plan: PlanType) => () => {
    if (plan.disabled) {
      return window.confirm(
        'Please contact our sales team to start a trial of the Accelerate plan.',
      )
        ? window.open('https://skedsocial.com/demo', '_blank')
        : null;
    }
    setSelectedPlan(plan);
    actions.updateFormAction({ selectedPlanId: plan.id });
  };

  const renderPriceWithSymbol = (
    plan: any,
    showBilledPerYear?: boolean,
    period?: 'year' | 'month',
  ) => {
    const symbol = CURRENCIES_PREFIX[formData.currency] || '$';
    let price = plan.price;

    if (period === 'month') {
      return `${symbol}${Number.parseFloat(price).toFixed(2).toLocaleString()}`;
    } else {
      if (showBilledPerYear) {
        price = (Number.parseFloat(plan.price) / 12).toFixed(2);
        return `${symbol}${price.toLocaleString()}`;
      } else {
        price = Number.parseFloat(plan.price).toFixed(2);
        return `${symbol}${price.toLocaleString()}`;
      }
    }
  };

  const shouldSkipCC = sessionStorage.getItem('skipCreditCardDetail') === 'true';
  return (
    <RootWrap>
      <PreviousNavWrap>
        <PreviousNav url="/account" />
      </PreviousNavWrap>
      {/* <TitleMobile>Create your Sked account</TitleMobile>
      <MobileMessage>
        <MobileMessageTitle>A quick note from the Sked team</MobileMessageTitle>
        Sked’s tools are optimized for desktop browsers. Please log in from your
        computer to access our platform after signing up for your free trial.
      </MobileMessage> */}
      <TrialContainer>
        <Wrap>
          <TitleMobile>Choose your plan</TitleMobile>
          <Title>Choose your plan</Title>
          <Subtitle>Save 2 months with yearly billing 🎉</Subtitle>
          <StyledAlert type={'info'}>
            Have a coupon code? Apply it in the next step after selecting your plan.
          </StyledAlert>
          <OptionsWrap>
            <CurrencyOptions>
              <h3>Currency</h3>
              <CurrencyOptionsWrap>
                {CURRENCIES.map(cur => {
                  return (
                    <OptionItem
                      active={formData.currency === cur.value}
                      key={cur.value}
                      onClick={() => handleChange('currency', cur.value)}
                    >
                      {cur.label}
                    </OptionItem>
                  );
                })}
              </CurrencyOptionsWrap>
            </CurrencyOptions>
            <BillingCycleOptions>
              <h3>Billing Cycle</h3>
              <BillingCycleOptionsWrap>
                {PERIODS.map(perd => (
                  <OptionItem
                    active={formData.period === perd.value}
                    key={perd.value}
                    onClick={() => {
                      handleChange('period', perd.value);
                      handleSwitchPricingType();
                    }}
                  >
                    {perd.label}
                  </OptionItem>
                ))}
              </BillingCycleOptionsWrap>
            </BillingCycleOptions>
          </OptionsWrap>
          <PlanWrap>
            {isGettingPlans ? (
              <SpinnerWrap>
                <Spinner />
              </SpinnerWrap>
            ) : (
              currentPlans.map((plan: PlanType, index) => {
                const { period } = formData;
                const planName = plan.shortName.toLowerCase();
                const isSelected = planName === selectedPlan?.shortName;
                const isYearly = period === 'year';
                const disabled = numberAccounts > 20;
                // @ts-ignore
                return (
                  <PlanComponent key={plan.shortName} amount={currentPlans.length}>
                    <Plan
                      selected={isSelected}
                      onClick={handlePlanClick(plan)}
                      showBilledPerYear={period === 'year'}
                    >
                      {!disabled && (
                        <PlanSelectedIndicator selected={isSelected}>
                          <span />
                        </PlanSelectedIndicator>
                      )}
                      <div className="mobileNamePrice">
                        <PlanName selected={isSelected}>{planName}</PlanName>
                        <PlanPrice selected={isSelected}>
                          <h4>
                            {!disabled
                              ? renderPriceWithSymbol(plan, isYearly, period)
                              : 'From US$399'}
                          </h4>
                          {!disabled && (
                            <PlanPriceDetail>
                              <span>/month</span>
                              <span>
                                {/* Show includedAccounts here as it will be the right number of max included accounts in signup */}
                                for {plan.includedAccounts ? plan.includedAccounts : numberAccounts}{' '}
                                social account
                                {((plan.includedAccounts
                                  ? plan.includedAccounts
                                  : numberAccounts) as number) > 1
                                  ? 's'
                                  : null}
                              </span>
                            </PlanPriceDetail>
                          )}
                        </PlanPrice>
                        {!disabled && (isYearly || plan.includesGst) && (
                          <BilledPerYear>
                            {isYearly && (
                              <span>
                                Billed as{' '}
                                <Strikethrough>
                                  {/* The math Looks really confusing but ends up being correct */}
                                  {renderPriceWithSymbol(
                                    { ...plan, price: (plan.price * 12) / 10 },
                                    false,
                                    'month',
                                  )}
                                </Strikethrough>{' '}
                                {renderPriceWithSymbol(plan, false, period)} /year
                                <br />
                              </span>
                            )}
                            {plan.includesGst && <span>(includes 10% GST)</span>}
                          </BilledPerYear>
                        )}
                      </div>
                      <FeaturesWrap>
                        <FeaturesTitle>
                          {index === 0 ? 'Tools that do the heavy lifting:' : plan.features?.title}
                        </FeaturesTitle>
                        <Features>
                          {plan.features?.value.map(feature => (
                            <li key={feature}>
                              <CheckIcon />
                              {feature}
                            </li>
                          ))}
                        </Features>
                      </FeaturesWrap>
                      {disabled && (
                        <Alert type={`success`}>
                          <p>
                            Please contact our sales team to start a trial of the Accelerate plan.
                          </p>
                        </Alert>
                      )}
                    </Plan>
                  </PlanComponent>
                );
              })
            )}
          </PlanWrap>
          <LinkSection>
            <ContactSection />
            <FullPricingLink href={'https://skedsocial.com/pricing'} target={'_blank'}>
              Full pricing <LinkExternalIcon />
            </FullPricingLink>
          </LinkSection>

          <ButtonWrapStyled>
            <ButtonStyled
              id="next"
              onClick={onSubmit}
              disabled={!isFormValid || isSubmittingForm}
              title={
                !isFormValid
                  ? 'Please answer all the questions to the left of the screen to continue.'
                  : ''
              }
            >
              {isSubmittingForm ? 'Please wait...' : shouldSkipCC ? 'Start free trial' : 'Continue'}
            </ButtonStyled>
            {shouldSkipCC && <span className="noCCRequired">No Credit Card Required!</span>}
          </ButtonWrapStyled>
        </Wrap>
      </TrialContainer>
    </RootWrap>
  );
};

const TrialContainer = styled(Container)`
  min-height: ${getContentHeightCss(52)};
  display: flex;
  align-items: center;
  justify-content: space-around;
`;
const StyledAlert = styled(Alert)`
  > div {
    margin: 15px 0;
    font-weight: 400;
  }
`;

export default ChooseTrial;
